import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Trophy, Gift, Award, Ticket, Lock } from 'lucide-react';
import api, { preferencesService } from '../../services/api';
import { userKey } from '../../utils/userStorage';
import RewardProgressBar from '../../components/common/RewardProgressBar';

// Niveau sélectionné mémorisé (même pattern que useExamPrefs : cache localStorage
// `nizus_dashboard_prefs` pour lecture instantanée + sync serveur via preferencesService).
const PREFS_STORAGE_KEY = 'nizus_dashboard_prefs';

function readVictoriesLastLevel() {
    try {
        const raw = localStorage.getItem(userKey(PREFS_STORAGE_KEY));
        return raw ? (JSON.parse(raw).victoriesLastLevel ?? null) : null;
    } catch {
        return null;
    }
}

function persistVictoriesLastLevel(levelVar) {
    try {
        const raw = localStorage.getItem(userKey(PREFS_STORAGE_KEY));
        const prefs = raw ? JSON.parse(raw) : {};
        localStorage.setItem(userKey(PREFS_STORAGE_KEY), JSON.stringify({ ...prefs, victoriesLastLevel: levelVar }));
    } catch { /* quota / private browsing */ }
    preferencesService.save({ victoriesLastLevel: levelVar }).catch(() => {});
}

// ── Promo code pill ──
const PromoCodePill = ({ ac, language }) => {
    const isUsed = parseInt(ac.isUsed);
    const isExpired = ac.expiresAt && new Date(ac.expiresAt) < new Date();
    const inactive = isUsed || isExpired;

    return (
        <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
            inactive
                ? 'bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-white/5 opacity-50'
                : 'bg-gradient-to-r from-amber-50/80 to-yellow-50/80 dark:from-amber-500/10 dark:to-yellow-500/10 border-amber-200/60 dark:border-amber-500/20'
        }`}>
            <Gift className={`w-4 h-4 flex-shrink-0 ${inactive ? 'text-gray-400' : 'text-amber-500'}`} />
            <span className={`font-mono font-bold text-sm ${
                inactive ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-amber-700 dark:text-amber-400'
            }`}>
                {ac.code}
            </span>
            <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold ${
                isUsed
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    : isExpired
                        ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400'
                        : 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
            }`}>
                {isUsed
                    ? (language === 'fr' ? 'Utilisé' : 'Used')
                    : isExpired
                        ? (language === 'fr' ? 'Expiré' : 'Expired')
                        : `-${ac.percentage}%`}
            </span>
            {ac.expiresAt && !isUsed && !isExpired && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                    Exp. {new Date(ac.expiresAt).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                </span>
            )}
        </div>
    );
};

// ── Badge card ──
const BadgeCard = ({ badge, color, language }) => {
    const label = language === 'fr' ? badge.labelFr : badge.labelEn;
    const desc  = language === 'fr' ? badge.descFr  : badge.descEn;

    return (
        <div className={`flex flex-col items-center text-center p-4 rounded-2xl border transition-all ${
            badge.unlocked
                ? 'bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-white/10'
                : 'bg-gray-50 dark:bg-[#111] border-gray-100 dark:border-white/5'
        }`}>
            {/* Badge image with lock overlay */}
            <div className="relative flex-shrink-0 mb-3">
                <img
                    src={`/img/application/rewards/${badge.key}.png`}
                    alt=""
                    className={`w-16 h-16 object-contain ${badge.unlocked ? '' : 'grayscale opacity-40'}`}
                />
                {!badge.unlocked && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gray-100 dark:bg-[#222] border border-gray-200 dark:border-white/10 flex items-center justify-center">
                        <Lock className="w-2.5 h-2.5 text-gray-400 dark:text-gray-500" />
                    </div>
                )}
            </div>

            {/* Info */}
            <div className="w-full">
                <p className={`text-sm font-black leading-tight ${
                    badge.unlocked ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
                }`}>
                    {label}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 leading-snug mt-1">
                    {desc}
                </p>

                {/* Progress bar for locked badges with target > 1 */}
                {!badge.unlocked && badge.target > 1 && (
                    <div className="mt-2">
                        <div className="w-full h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                            <div
                                className="h-full rounded-full transition-all"
                                style={{
                                    width: `${Math.min((badge.progress / badge.target) * 100, 100)}%`,
                                    backgroundColor: color,
                                }}
                            />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {Math.round(badge.progress)}/{badge.target}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ── Main component ──
const Victories = () => {
    const { language } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedLevelVar, setSelectedLevelVar] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const result = await api.getRewardsSummary();
                setData(result);
                // Restore last selected level if still available, else first level
                // with history, or first active, or first overall
                const saved = readVictoriesLastLevel();
                const savedLevel = saved ? result?.levels?.find(l => l.levelVar === saved) : null;
                const firstWithHistory = result?.levels?.find(l => l.active || l.count > 0);
                const first = savedLevel || firstWithHistory || result?.levels?.[0];
                if (first) setSelectedLevelVar(first.levelVar);
            } catch (e) {
                console.error('Failed to load rewards summary', e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) {
        return <div className="text-center py-12 text-gray-500">{language === 'fr' ? 'Chargement...' : 'Loading...'}</div>;
    }

    const levels           = data?.levels ?? [];
    const globalCount      = data?.globalCount ?? 0;
    const globalTotal      = data?.globalTotal ?? 0;
    const achievementCodes = data?.achievementCodes ?? [];
    const activeCodes      = achievementCodes.filter(ac => !parseInt(ac.isUsed) && !(ac.expiresAt && new Date(ac.expiresAt) < new Date()));

    const selectedLevel = levels.find(l => l.levelVar === selectedLevelVar) ?? levels[0];
    const levelCodes    = achievementCodes.filter(c => c.levelVar === selectedLevel?.levelVar);

    return (
        <div className="pb-12">
            {/* Hero Banner */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#795548] via-[#5d4037] to-[#3e2723] border border-white/5 !mb-10 shadow-xl">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }} />
                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <img src="/img/application/Victoire.png" alt="" className="w-10 h-10 object-contain" />
                        <h1 className="text-4xl font-black text-white tracking-tight">
                            {language === 'fr' ? 'Mes victoires' : 'My victories'}
                        </h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-sm leading-snug">
                        {language === 'fr'
                            ? 'Débloque des badges pour obtenir des codes promo'
                            : 'Unlock badges to earn promo codes'}
                    </p>
                </div>
                <img src="/img/application/Victoire.png" alt="" className="absolute right-[-20px] bottom-[-20px] w-72 h-72 object-contain opacity-20 pointer-events-none" />
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-3 mb-8">
                <div className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 p-4 text-center">
                    <Trophy className="w-5 h-5 text-amber-500 mx-auto mb-1.5" />
                    <p className="text-2xl font-black text-gray-900 dark:text-white">
                        {globalCount}<span className="text-sm font-bold text-gray-400 dark:text-gray-500">/{globalTotal}</span>
                    </p>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-0.5">
                        {language === 'fr' ? 'Badges' : 'Badges'}
                    </p>
                </div>
                <div className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 p-4 text-center">
                    <Ticket className="w-5 h-5 text-green-500 mx-auto mb-1.5" />
                    <p className="text-2xl font-black text-gray-900 dark:text-white">{achievementCodes.length}</p>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-0.5">
                        {language === 'fr' ? 'Codes obtenus' : 'Codes earned'}
                    </p>
                </div>
                <div className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 p-4 text-center">
                    <Award className="w-5 h-5 text-blue-500 mx-auto mb-1.5" />
                    <p className="text-2xl font-black text-gray-900 dark:text-white">{activeCodes.length}</p>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-0.5">
                        {language === 'fr' ? 'Codes actifs' : 'Active codes'}
                    </p>
                </div>
            </div>

            {/* Level selector */}
            {levels.length > 0 && (
                <div className="overflow-x-auto no-scrollbar mb-6">
                    <div className="flex gap-2 pb-1">
                        {levels.map(level => {
                            const label      = language === 'fr' ? level.labelFr : level.labelEn;
                            const isSelected = selectedLevelVar === level.levelVar;
                            return (
                                <button
                                    key={level.levelVar}
                                    onClick={() => { setSelectedLevelVar(level.levelVar); persistVictoriesLastLevel(level.levelVar); }}
                                    className={`flex-shrink-0 rounded-xl px-4 py-2 text-[11px] font-black uppercase tracking-wide transition-all ${
                                        isSelected
                                            ? 'text-white shadow-md'
                                            : (level.active || level.count > 0)
                                                ? 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:opacity-80'
                                                : 'bg-gray-100 dark:bg-white/5 text-gray-300 dark:text-gray-600 opacity-40 hover:opacity-60'
                                    }`}
                                    style={isSelected ? { backgroundColor: level.color } : {}}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Selected level content */}
            {selectedLevel && (
                <>
                    {/* Progress bar */}
                    {(selectedLevel.active || selectedLevel.count > 0) && (
                        <div className="mb-5">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: selectedLevel.color }} />
                                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                        {language === 'fr' ? selectedLevel.labelFr : selectedLevel.labelEn}
                                    </span>
                                </div>
                                <span className="text-xs font-bold text-gray-400 dark:text-gray-500">
                                    {selectedLevel.count}/{selectedLevel.total} badges
                                </span>
                            </div>
                            {selectedLevel.rewardTiers?.length > 0 && (
                                <RewardProgressBar
                                    count={selectedLevel.count}
                                    total={selectedLevel.total}
                                    rewardTiers={selectedLevel.rewardTiers}
                                    color={selectedLevel.color}
                                    size="md"
                                    variant="card"
                                />
                            )}
                        </div>
                    )}

                    {/* Badge grid */}
                    {selectedLevel.badges?.length > 0 ? (
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            {selectedLevel.badges.map(badge => (
                                <BadgeCard
                                    key={badge.key}
                                    badge={badge}
                                    color={selectedLevel.color}
                                    language={language}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-10 mb-6">
                            <p className="text-gray-400 dark:text-gray-500 text-sm">
                                {language === 'fr' ? 'Aucun badge disponible.' : 'No badges available.'}
                            </p>
                        </div>
                    )}

                    {/* Promo codes for this level */}
                    {levelCodes.length > 0 && (
                        <div className="space-y-2 mb-6">
                            <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                                {language === 'fr' ? 'Codes de récompense' : 'Reward codes'}
                            </p>
                            {levelCodes.map((ac, i) => (
                                <PromoCodePill key={i} ac={ac} language={language} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Victories;
